import { useState, useCallback } from "react";
import { calculateDurationDiff, calculatePriceDiff, formatPriceDiff, toHHMM } from "../serviceChangesCalculations";

interface ServiceValues {
    duration?: string;
    price?: number;
    online?: boolean;
}

interface DurationChange {
    oldValue: string;
    newValue: string;
    diff: string;
}

interface PriceChange {
    oldValue: number;
    newValue: number;
    diff: number;
    formattedDiff: string;
}

interface OnlineChange {
    oldValue: boolean;
    newValue: boolean;
}

export interface ServiceChanges {
    duration?: DurationChange;
    price?: PriceChange;
    online?: OnlineChange;
}

export const useServiceChangesTracker = () => {
    const [oldValues, setOldValues] = useState<ServiceValues>({});

    const captureOldValues = useCallback((values: ServiceValues) => {
        setOldValues(values);
    }, []);

    const calculateChangesWithNewValues = useCallback(
        (newValues: ServiceValues) => {
            const changes: ServiceChanges = {};

            const oldDuration = oldValues.duration ? toHHMM(oldValues.duration) : undefined;
            const newDuration = newValues.duration ? toHHMM(newValues.duration) : undefined;

            if (oldDuration !== undefined && newDuration !== undefined && oldDuration !== newDuration) {
                changes.duration = {
                    oldValue: oldDuration,
                    newValue: newDuration,
                    diff: calculateDurationDiff(oldDuration, newDuration),
                };
            }

            if (oldValues.price !== undefined && newValues.price !== undefined && oldValues.price !== newValues.price) {
                const diff = calculatePriceDiff(oldValues.price, newValues.price);

                changes.price = {
                    oldValue: oldValues.price,
                    newValue: newValues.price,
                    diff,
                    formattedDiff: formatPriceDiff(diff),
                };
            }

            if (
                oldValues.online !== undefined &&
                newValues.online !== undefined &&
                oldValues.online !== newValues.online
            ) {
                changes.online = {
                    oldValue: oldValues.online,
                    newValue: newValues.online,
                };
            }

            return changes;
        },
        [oldValues]
    );

    return {
        captureOldValues,
        calculateChangesWithNewValues,
    };
};
